import { useCallback, useEffect, useRef } from 'react';

/**
 * Returns a stable-ish callback that invokes `fn` after `delayMs` of quiet — each call resets the
 * timer, and the pending call is cancelled on unmount. The list pages use this to debounce
 * server-side prefix filters (users email, homes name) behind their text inputs.
 */
export function useDebouncedCallback<Args extends unknown[]>(fn: (...args: Args) => void, delayMs = 300) {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const fnRef = useRef(fn);
    fnRef.current = fn;

    useEffect(() => {
        return () => clearTimeout(timeoutRef.current);
    }, []);

    return useCallback(
        (...args: Args) => {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => fnRef.current(...args), delayMs);
        },
        [delayMs],
    );
}
